import {
    FOCUS_RING,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useEffect, useRef, useState } from 'react';

import type { KeyboardEvent, MouseEvent } from 'react';

import { KEY_RESULT_FILL } from '@/components/objectives/objectiveStyles';
import i18n from '@/plugins/i18n';
import { formatPercent } from '@/utils/numberFormat';
import { clampPercent } from '@/utils/objectiveProgress';

/** The form's own cursor colour, the default for every caller that does not override it. */
const DEFAULT_CURSOR_CLASS = 'bg-navy/40';

/** Arrow-key nudges are grouped into a single commit instead of firing `onChange` on every
 * keypress: the local value moves right away for visual feedback, and `onChange` only fires this
 * long after the last key press. */
const ARROW_KEY_COMMIT_DELAY_MS = 400;

interface KeyResultProgressBarProps {
    /** 0-100, already clamped by the caller. */
    percentage: number;
    /** Absent renders a read-only bar: no cursor, no keyboard handling, no hover tooltip. A
     * returned promise keeps the chosen percentage on screen until it settles; either way the bar
     * then shows `percentage` again, so a change that was not applied never stays drawn. */
    onChange?: (percentage: number) => void | Promise<unknown>;
    /** Turquoise `KEY_RESULT_FILL` once true, the in-progress `bg-obj-accent` otherwise. */
    complete?: boolean;
    /** The measurement's own name, exposed to assistive technology as the bar's accessible name. */
    label: string;
    dataTestId?: string;
    /** Overrides the track's height -- `h-2.5` on the form, `.obj-kr-track-detail` (plain CSS, 8px
     * -> 11px at md, `app.css`) on the detail page: a base utility beats its own `md:` variant in
     * this Tailwind v4 build, the same flat-cascade reason as `.obj-header`. */
    className?: string;
    /** Overrides the hover cursor's colour -- the detail page uses the primary token
     * (`bg-primary/70`) instead of the form's `bg-navy/40` default, a turquoise cursor. */
    cursorClassName?: string;
    /** Words the hover tooltip from the percentage under the pointer. The detail page previews the
     * value a drop there would set, in the measurement's own unit ("36", "36 %", "OUI"), which is
     * what the reader is about to commit; left out, the bar previews the percentage itself. */
    formatHoverValue?: (percentage: number) => string;
    /** Blocks click, keyboard and hover interaction while a commit is in flight -- the caller
     * passes `isPending` from `useKeyResultProgressCommit`, mirroring `KeyResultProgressControl`'s
     * own numeric field and stepper. The bar keeps its `slider` role while disabled: it
     * is still a slider, just temporarily inert. */
    disabled?: boolean;
}

const percentageFromPointer = (clientX: number, rect: { left: number; width: number }): number => {
    if (rect.width <= 0) {
        return 0;
    }

    return clampPercent(((clientX - rect.left) / rect.width) * 100);
};

/**
 * The clickable key-result progress bar, shared by the form (`KeyResultFields`) and the detail
 * page (`ObjectiveDetailMeasurements`): a click or a keypress reports a new percentage through
 * `onChange`, and hovering previews it with a cursor line and a tooltip. Rendered read-only
 * (no `onChange`) it is a plain, non-interactive bar.
 */
export default function KeyResultProgressBar({
    percentage,
    onChange,
    complete = false,
    label,
    dataTestId,
    className,
    cursorClassName = DEFAULT_CURSOR_CLASS,
    formatHoverValue,
    disabled = false,
}: KeyResultProgressBarProps) {
    const [hoverPercentage, setHoverPercentage] = useState<number | null>(null);
    // The percentage chosen but not yet reflected by `percentage`: an arrow-key nudge waiting for
    // its grouped commit, or a commit still in flight. `null` means the bar shows the caller's
    // value, which stays the only source of truth once a commit has settled.
    const [pendingPercentage, setPendingPercentage] = useState<number | null>(null);
    // Bumped on every new choice, so a commit that settles late never clears a newer one.
    const choiceRef = useRef(0);
    const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const displayedPercentage = pendingPercentage ?? percentage;
    const hasOnChange = !!onChange;
    const isInteractive = hasOnChange && !disabled;
    const lang = i18n.language;

    // Clears a still-pending debounced commit on unmount, so it never fires `onChange` after the
    // bar is gone.
    useEffect(() => () => clearTimeout(commitTimeoutRef.current), []);

    const choose = (nextPercentage: number): number => {
        clearTimeout(commitTimeoutRef.current);
        choiceRef.current += 1;
        setPendingPercentage(nextPercentage);
        return choiceRef.current;
    };

    const commit = async (nextPercentage: number, choice: number) => {
        try {
            await onChange?.(nextPercentage);
        } finally {
            if (choice === choiceRef.current) {
                setPendingPercentage(null);
            }
        }
    };

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        const nextPercentage = percentageFromPointer(
            event.clientX,
            event.currentTarget.getBoundingClientRect(),
        );
        commit(nextPercentage, choose(nextPercentage));
    };

    const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
        if (!isInteractive) {
            return;
        }

        setHoverPercentage(
            percentageFromPointer(event.clientX, event.currentTarget.getBoundingClientRect()),
        );
    };

    const handleMouseLeave = () => setHoverPercentage(null);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!onChange) {
            return;
        }

        let nextPercentage: number | undefined;

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            nextPercentage = clampPercent(displayedPercentage + 1);
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            nextPercentage = clampPercent(displayedPercentage - 1);
        } else if (event.key === 'Home') {
            event.preventDefault();
            nextPercentage = 0;
        } else if (event.key === 'End') {
            event.preventDefault();
            nextPercentage = 100;
        }

        if (nextPercentage === undefined) {
            return;
        }

        const choice = choose(nextPercentage);
        commitTimeoutRef.current = setTimeout(
            () => commit(nextPercentage, choice),
            ARROW_KEY_COMMIT_DELAY_MS,
        );
    };

    return (
        <div
            role={hasOnChange ? 'slider' : 'progressbar'}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={displayedPercentage}
            aria-label={label}
            aria-disabled={hasOnChange ? disabled : undefined}
            tabIndex={isInteractive ? 0 : undefined}
            onClick={isInteractive ? handleClick : undefined}
            onMouseMove={isInteractive ? handleMouseMove : undefined}
            onMouseLeave={isInteractive ? handleMouseLeave : undefined}
            onKeyDown={isInteractive ? handleKeyDown : undefined}
            className={`bg-obj-tint relative flex-1 overflow-hidden rounded-full shadow-sm ${
                isInteractive
                    ? `cursor-pointer ${FOCUS_RING}`
                    : hasOnChange && disabled
                      ? 'cursor-default'
                      : ''
            } ${className ?? ''}`}
            data-testid={dataTestId}
        >
            <div
                className={`absolute top-0 left-0 h-full rounded-full transition-[width] duration-500 ${
                    complete ? KEY_RESULT_FILL : 'bg-obj-accent'
                }`}
                style={{ width: `${displayedPercentage}%` }}
            />

            {isInteractive && hoverPercentage !== null && (
                <TooltipProvider delayDuration={0}>
                    <Tooltip open>
                        <TooltipTrigger asChild>
                            <div
                                className={`absolute -top-1 h-4.5 w-0.5 ${cursorClassName} pointer-events-none`}
                                style={{
                                    left: `${hoverPercentage}%`,
                                    transform: 'translateX(-50%)',
                                }}
                                data-testid={
                                    dataTestId ? `${dataTestId}-cursor` : 'kr-progress-bar-cursor'
                                }
                            />
                        </TooltipTrigger>

                        <TooltipContent side={'top'}>
                            {formatHoverValue?.(hoverPercentage) ??
                                formatPercent(hoverPercentage, lang)}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
}
